import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaMountain, FaSnowflake
} from 'react-icons/fa';
import './KashmirTimelessEscape.css';

const KashmirTimelessEscape = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  // KASHMIR ITINERARY DATA
  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Srinagar & Mughal Garden Tour",
      points: [
        "Arrival at Srinagar Airport and transfer to hotel.",
        "Visit famous Mughal Gardens: Nishat Bagh, Shalimar Bagh, and Chashme Shahi.",
        "Explore Pari Mahal for a panoramic viewpoint overlooking Dal Lake.",
        "Evening: Enjoy a relaxing Shikara ride on Dal Lake.",
        "Dinner and overnight stay in Srinagar."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Gulmarg Excursion (Meadow of Flowers)",
      points: [
        "Full-day excursion to Gulmarg, surrounded by snow-capped mountains.",
        "Experience the famous Gondola Cable Car Ride (Phase 1) for Himalayan views.",
        "Optional: Enjoy snow activities (seasonal), ATV rides, or Pony rides.",
        "Explore lush alpine meadows and capture beautiful photographs.",
        "Return to Srinagar for dinner and overnight stay."
      ],
      icon: <FaSnowflake />
    },
    {
      day: 3,
      title: "Sonmarg Excursion (Meadow of Gold)",
      points: [
        "Depart for a scenic drive to Sonmarg through valleys and alpine forests.",
        "Explore the picturesque valley surrounded by glaciers.",
        "Optional: Pony ride to visit the Thajiwas Glacier viewpoint.",
        "Spend time enjoying the natural beauty of the Sindh River region.",
        "Return to Srinagar for dinner and overnight stay."
      ],
      icon: <FaMountain />
    },
    {
      day: 4,
      title: "Srinagar to Pahalgam (Valley of Shepherds)",
      points: [
        "Check out and drive to Pahalgam through saffron fields and Kashmiri villages.",
        "Enjoy the scenic beauty along the Lidder River.",
        "Evening: Free for a peaceful walk or exploring local surroundings.",
        "Dinner and overnight stay in Pahalgam."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 5,
      title: "Pahalgam Valley Sightseeing (ABC Tour)",
      points: [
        "Proceed for the famous ABC Valley Tour: Aru Valley, Betaab Valley, and Chandanwari.",
        "Experience the lush greenery and flowing rivers of these iconic film locations.",
        "Ample time for photography and relaxing in nature.",
        "Dinner and overnight stay in Pahalgam."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 6,
      title: "Pahalgam to Srinagar & City Exploration",
      points: [
        "Return to Srinagar and visit the Shankaracharya Temple for city views.",
        "Visit the Indira Gandhi Memorial Tulip Garden (seasonal).",
        "Leisure time at Dal Lake Boulevard to explore local markets and handicrafts.",
        "Final dinner and overnight stay in Srinagar."
      ],
      icon: <FaBus />
    },
    {
      day: 7,
      title: "Departure",
      points: [
        "Breakfast and hotel check-out.",
        "Transfer to Srinagar Airport for your onward journey.",
        "Tour concludes with unforgettable memories of Paradise on Earth."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Kashmir Timeless Escape – <br /> Srinagar, Gulmarg, Sonmarg & Pahalgam</h1>
          <p className="trip-duration">Duration: 6 nights & 7 days of Alpine Beauty</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Srinagar, Gulmarg, Sonmarg, Pahalgam</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Leisure & Scenic Tour</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>Paradise on Earth</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1600845747913-e33543f94892?q=80&w=944&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Dal Lake" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://images.unsplash.com/photo-1595815771614-ade9d652a65d?q=80&w=1200" alt="Gulmarg Snow" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1666501544693-746534d83625?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Pahalgam Valley" />
          </div>
          <div className="grid-item small-h">
            <img src="https://www.tourmyindia.com/states/jammu-kashmir/image/thajiwas-glacier-sonmarg.jpg" alt="Sonmarg Glacier" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>Overview</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Day Wise Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Traditional Shikara Ride on the serene Dal Lake</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Scenic Gulmarg Gondola Cable Car Ride (Phase 1)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>ABC Valley Tour: Aru, Betaab & Chandanwari in Pahalgam</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Breathtaking Sonmarg Valley and Thajiwas Glacier region</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore historic Mughal Gardens & Shankaracharya Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the seasonal Indira Gandhi Memorial Tulip Garden</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> 6 Nights stay in comfortable hotels (4N Srinagar | 2N Pahalgam)</li>
              <li><FaUtensils /> Meal Plan: Daily Breakfast and Dinner at hotels</li>
              <li><FaBus /> All sightseeing and transfers in a private vehicle</li>
              <li><FaStar /> Traditional Shikara ride on Dal Lake</li>
              <li><FaSnowflake /> Gulmarg Gondola Cable Ride – Phase 1 Tickets</li>
              <li><FaGlobeAmericas /> Pahalgam ABC Valley sightseeing (Aru, Betaab & Chandanwari)</li>
              <li><FaCheckCircle /> Entry tickets for Mughal Gardens and Tulip Garden</li>
              <li><FaShieldAlt /> Driver charges, fuel, toll, and permits</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets</li>
              <li><FaTimesCircle /> Pony rides, ATV rides, or snow activities</li>
              <li><FaTimesCircle /> Camera or video charges at tourist attractions</li>
              <li><FaTimesCircle /> Personal expenses (Shopping, Tips, Laundry)</li>
              <li><FaShieldAlt /> Travel insurance and medical expenses</li>
              <li><FaTimesCircle /> Anything not mentioned in inclusions</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Tour Overview</h2>
          <p className="about-text">
            The Kashmir Timeless Escape offers a mesmerizing journey through the breathtaking landscapes of Kashmir, often referred to as “Paradise on Earth.” This tour covers the region’s most iconic destinations including Srinagar, Gulmarg, Sonmarg, and Pahalgam, combining scenic beauty, cultural heritage, and relaxing experiences.
            <br /><br />
            Travelers will explore the historic Mughal Gardens, enjoy a peaceful Shikara ride, experience the spectacular Gondola in Gulmarg, and visit the picturesque valleys of Pahalgam. With comfortable travel and balanced sightseeing, this tour provides the perfect opportunity to experience the charm and culture of Kashmir.
          </p>
        </motion.section>

        {/* FOOTER LINKS */}
        <div className="footer-links-container" style={{marginTop: '40px'}}>
          <div className="accordion-item">
            <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Terms & Conditions</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
            </Link>
          </div>

          <div className="accordion-item">
            <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Privacy Policy</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
            </Link>
          </div>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{width: '100%'}}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares to ensure a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default KashmirTimelessEscape;